import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Home,
  Bed,
  User,
  MapPin,
  Building2,
  Shirt,
  Package,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchHospedajeById } from '@/services/hospedajes.service';
import { useAuth } from '@/contexts/AuthContext';

const HospedajeDashboard = () => {
  const { hospedajeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [hospedaje, setHospedaje] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const { user } = useAuth();

  // Cargar datos del hospedaje - primero intenta usar datos del state, luego fetch si es necesario
  useEffect(() => {
    const loadHospedaje = async () => {
      // Si vienen datos del state de navegación, usarlos directamente
      if (location.state?.hospedaje) {
        setHospedaje(location.state.hospedaje);
        setIsLoading(false);
        return;
      }

      // Si no hay datos en el state, hacer fetch (fallback)
      setIsLoading(true);
      try {
        const data = await fetchHospedajeById(hospedajeId);
        setHospedaje(data);
      } catch (error) {
        console.error('Error al cargar hospedaje:', error);
        toast({
          title: "Error",
          description: "No se pudo cargar la información del hospedaje. Intenta volver y seleccionar el hospedaje nuevamente.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (hospedajeId) {
      loadHospedaje();
    }
  }, [hospedajeId, location.state, toast]);

  const handleNavigation = (item) => {
    if (!item.isReady) {
      toast({
        title: 'En desarrollo',
        description: 'Esta funcionalidad estará disponible próximamente',
      });
      return;
    }

    // Navegación específica según el item
    switch (item.id) {
      case 'inventario-lenceria':
        navigate(`/hospedaje/${hospedajeId}/inventario-lenceria`);
        break;
      case 'solicitar-lenceria':
        navigate(`/hospedaje/${hospedajeId}/solicitar-lenceria`);
        break;
      case 'solicitar-insumos':
        // Navegar con el ID del edificio en la ruta y pasar datos completos por state
        if (hospedaje?.edificio) {
          navigate(`/solicitudes/insumos/${hospedaje.edificio.id || hospedaje.edificio._id}`, { 
            state: { 
              edificio: {
                id: hospedaje.edificio.id || hospedaje.edificio._id,
                nombre: hospedaje.edificio.nombre,
                ciudad: hospedaje.edificio.ciudad,
                direccion: hospedaje.edificio.direccion,
                tipoLogistica: hospedaje.edificio.tipoLogistica || 'horizontal'
              },
              hospedaje: {
                id: hospedaje.id || hospedaje._id,
                nombre: hospedaje.nombrePropiedad,
                tipo: hospedaje.tipo
              }
            } 
          });
        } else {
          toast({
            title: "Error",
            description: "No se encontró información del edificio",
            variant: "destructive"
          });
        }
        break;
      case 'inventario-habitacion':
        navigate(`/hospedaje/${hospedajeId}/inventario-habitacion`);
        break;
      default:
        toast({
          title: "Navegando",
          description: `Accediendo a ${item.title}`,
        });
    }
  };

  const handleSelectLenceria = (selectedItem) => {
    setSelectedItems(prev => [...prev, selectedItem]);
  };

  const handleSelectInsumo = (selectedItem) => {
    setSelectedItems(prev => [...prev, selectedItem]);
  };

  const handleSaveSolicitudLenceria = async (edificioParam) => {
    if (selectedItems.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un elemento",
        variant: "destructive",
      });
      return;
    }

    try {
      const total_solicitado = selectedItems.reduce((sum, item) => sum + item.cantidad, 0);

      // Usar el edificio del hospedaje (ya lo tenemos en el contexto)
      const edificioData = hospedaje.edificio;

      const solicitudData = {
        creator: {
          id: user.id,
          nombre: user.name
        },
        edificio: {
          id: edificioData?.id || edificioData?._id,
          nombre: edificioData?.nombre,
          ciudad: edificioData?.ciudad
        },
        total_solicitado: total_solicitado,
        total_entregado: 0,
        items: selectedItems.map(item => ({
          id: item.garmentTypeId || item.id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          peso: item.peso,
          observacion: item.observacion
        })),
        observaciones: '',
        estado: 'Pendiente'
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(solicitudData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al crear la solicitud');
      }

      toast({
        title: "Solicitud Creada",
        description: "La solicitud de lencería ha sido registrada exitosamente",
      });

      setIsLenceriaModalOpen(false);
      setSelectedItems([]);
    } catch (error) {
      console.error('Error al crear solicitud:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo crear la solicitud",
        variant: "destructive",
      });
    }
  };

  const handleSaveSolicitudInsumos = async (edificioParam) => {
    if (selectedItems.length === 0) {
      toast({
        title: "Error",
        description: "Debe seleccionar al menos un elemento",
        variant: "destructive",
      });
      return;
    }

    try {
      const total_solicitado = selectedItems.reduce((sum, item) => sum + item.cantidad, 0);

      // Usar el edificio del hospedaje (ya lo tenemos en el contexto)
      const edificioData = hospedaje.edificio;

      const solicitudData = {
        creator: {
          id: user.id,
          nombre: user.name
        },
        edificio: {
          id: edificioData?.id || edificioData?._id,
          nombre: edificioData?.nombre,
          ciudad: edificioData?.ciudad
        },
        total_solicitado: total_solicitado,
        total_entregado: 0,
        items: selectedItems.map(item => ({
          id: item.insumoId || item.id,
          nombre: item.nombre,
          cantidad: item.cantidad,
          tipo: item.tipo,
          observacion: item.observacion
        })),
        observaciones: '',
        estado: 'Pendiente'
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(solicitudData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al crear la solicitud');
      }

      toast({
        title: "Solicitud Creada",
        description: "La solicitud de insumos ha sido registrada exitosamente",
      });

      setIsInsumosModalOpen(false);
      setSelectedItems([]);
    } catch (error) {
      console.error('Error al crear solicitud:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo crear la solicitud",
        variant: "destructive",
      });
    }
  };

  const getEstadoOcupacionBadge = (estado) => {
    const variants = {
      'Disponible': 'bg-green-100 text-green-800',
      'Ocupado': 'bg-blue-100 text-blue-800',
      'Mantenimiento': 'bg-orange-100 text-orange-800',
      'Limpieza': 'bg-purple-100 text-purple-800'
    };

    return (
      <Badge className={`text-xs ${variants[estado] || 'bg-gray-100 text-gray-800'}`}>
        {estado}
      </Badge>
    );
  };

  const getTipoHabitacionLabel = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'entire_home':
        return 'Casa/Apartamento completo';
      case 'private_room':
        return 'Habitación privada';
      case 'shared_room':
        return 'Habitación compartida';
      default:
        return tipo || 'No especificado';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando dashboard del hospedaje...</p>
        </div>
      </div>
    );
  }

  if (!hospedaje) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Home className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Activos no encontrado
          </h3>
          <p className="text-gray-500 mb-4">
            No se pudo encontrar la información del activo solicitado
          </p>
          <Button onClick={() => navigate('/propiedades')}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  const dashboardItems = [
    {
      id: 'solicitar-lenceria',
      title: 'Solicitar Lencería (Horizontal)',
      description: 'Crear solicitudes de lencería',
      icon: Shirt,
      color: 'bg-blue-100 text-blue-600',
      hoverColor: 'hover:bg-blue-50 hover:border-blue-300',
      isReady: true
    },
    {
      id: 'solicitar-insumos',
      title: 'Solicitar Insumos (Horizontal)',
      description: 'Crear solicitudes de insumos',
      icon: Package,
      color: 'bg-purple-100 text-purple-600',
      hoverColor: 'hover:bg-purple-50 hover:border-purple-300',
      isReady: true
    },
    {
      id: 'inventario-habitacion',
      title: 'Inventario de Habitación',
      description: 'Ver y gestionar las camas parametrizadas del activo',
      icon: Bed,
      color: 'bg-orange-100 text-orange-600',
      hoverColor: 'hover:bg-orange-50 hover:border-orange-300',
      isReady: true
    },

  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/propiedades/hospedajes/${hospedaje?.edificio?.id || hospedaje?.edificio?._id}`, {
                  replace: true,
                  state: { key: Date.now() }
                })}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver a Activos </span>
              </Button>
            </div>
            {getEstadoOcupacionBadge(hospedaje.estadoOcupacion)}
          </div>

          <div className="flex items-center space-x-4 pt-4 border-t">
            <Home className="h-8 w-8 text-foreground" />
            <div className="flex-1">
              <CardTitle className="text-2xl font-bold text-foreground">
                {hospedaje.nombrePropiedad}
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                <div className="flex items-center space-x-4 mt-2">
                  <span className="flex items-center space-x-1">
                    <Building2 className="h-4 w-4" />
                    <span>{hospedaje.edificio.nombre}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <MapPin className="h-4 w-4" />
                    <span>{hospedaje.edificio.direccion || 'No disponible'} {hospedaje.edificio.ciudad}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{hospedaje.coordinador.nombre}</span>
                  </span>
                </div>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Dashboard Grid */}
      <Card>

        <CardHeader>
          <CardTitle className="text-lg font-semibold">Acciones Rápidas</CardTitle>
          <CardDescription>Gestiona operaciones comunes para este portafolio horizontal</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dashboardItems.map((item, index) => (
              <Button
                key={item.id}
                variant="outline"
                className={`h-auto flex flex-col items-center justify-center p-6 space-y-3 ${item.hoverColor} `}
                onClick={() => handleNavigation(item)}
              >
                <div className={`p-3 rounded-full ${item.color}`}>
                  <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">{item.title}</div>
                  <div className="text-xs text-gray-500 mt-1">{item.description}</div>
                </div>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default HospedajeDashboard;
